/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { safeMarkdownPlugins, safeMarkdownComponents } from "../../lib/utils/safeMarkdown.mjs";
import { useChatbotController } from "../../hooks/useChatbotController.jsx";
import { useInactivityTracker } from "../../hooks/useInactivityTracker.jsx";
import { clearChatLocalStorage } from "../../lib/helper/clearChatLocalStorage.js";
import { EndChat } from "../EndChat";
import { ChatBotEndChat } from "../EndChat/ChatBotEndChat.jsx";
import Cancel from "../Icons/Cancel";
import ChevronDown from "../Icons/ChevronDown";
import Email from "../Icons/Email.jsx";
import Maximize2 from "../Icons/Maximize-2.jsx";
import Minimize2 from "../Icons/Minimize-2.jsx";
import PeopleIcon from "../Icons/PeopleIcon";
import { LinkRenderer } from "../LinkRenderer/index.js";
import { TermsAndConditions } from "../TermsAndConditions";
import Transcript from "../Transcript";
import InputTextArea from "./InputTextArea.jsx";
import { SystemMessage } from "./SystemMessage";
import { UserMessage } from "./UserMessage";
import "./chat.css";

const hoverNoneCss = "hover:curai-bg-white";

export const ChatContainer = ({
  apiKey,
  widgetKey,
  isChatWindowOpen,
  handleIsChatWindowOpen,
  setIsMaximizeChatSize,
  isMaximizeChatSize,
  handleChatToggle,
  chatBotFeatures,
  branding,
  isBrandLoading,
}) => {
  const {
    chatCreate,
    isLoading,
    currentAssistantChatMessage,
    isStreamComplete,
    chatSessionHistory,
    currentUserChatMessage,
    hasChatEnded,
    liveChatsData,
    step,
    chatHistory,
    setChatHistory,
    handleChatCreate,
    setHasChatEnded,
    handleUserData,
    setChatValue,
    setCurrentUserChatMessage,
    setCurrentAssistantChatMessage,
    setLiveChatsData,
    setChatCreate,
    setChatSessionHistory,
    setIsRequestTypeAgent,
    setStep,
    sendMessageContainerRef,
    isRequestTypeAgent,
    chatValue,
    disconnectAgentChat,
    setTextareaHeight,
    setTextareaMaximizeHeight,
    textareaHeight,
    textareaMaximizeHeight,
    userAgentDetails,
    getUserAgentDetails,
    setOrgId,
    orgId,
    isDisconnectChatLoading,
  } = useChatbotController(widgetKey, apiKey);

  const [isTranscript, setIsTranscript] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState(
    chatBotFeatures.isTermsConditionHide ?? false,
  );
  const [sessionChatHistory, setSessionChatHistory] = useState([]);
  const [isLandscape, setIsLandscape] = useState(false);

  const { message, tnc, colorOne, colorTwo, title, logoUrl } = branding || {};
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const agentName = localStorage.getItem("agentName");
  const agentFirstName = agentName?.split(" ")[0];

  useEffect(() => {
    if (
      liveChatsData.length > 0 ||
      chatHistory.length > 0 ||
      currentAssistantChatMessage !== "" ||
      currentUserChatMessage !== ""
    ) {
      scrollToBottom();
    }
  }, [
    liveChatsData,
    chatHistory,
    currentAssistantChatMessage,
    currentUserChatMessage,
  ]);

  useEffect(() => {
    if (localStorage.getItem("cid")) {
      const cid = localStorage.getItem("cid");
      const oid = localStorage.getItem("oid");
      setOrgId(oid);
      setAcceptTermsAndConditions(true);
      setChatCreate(cid);
    }

    if (
      localStorage.getItem("chatMessage") !== "" &&
      localStorage.getItem("chatMessage") !== null
    ) {
      const message = JSON.parse(localStorage.getItem("chatMessage"));
      setChatSessionHistory(message);
      // setChatSessionHistory(message);
      setSessionChatHistory(message);
    }

    // After reload we can get the chats from local storage
    const type = localStorage.getItem("typeAgent");
    const userChat = localStorage.getItem("userAgentChats");
    const currentStep = localStorage.getItem("step");
    const convertedArray = JSON.parse(userChat);
    if (type !== null && currentStep > 0 && convertedArray.length > 0) {
      setIsRequestTypeAgent(type);
      setLiveChatsData(convertedArray);
      setStep(Number(currentStep));
    }
    // After reload we can get the chats from local storage
    let userDetails = {};
    try {
      userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {}

    if (userDetails?.customer_email && userDetails?.customer_name && userDetails?.message) {
      setIsRequestTypeAgent("live_agent_request");
    }
    // check the chat is expired or not

    //For landscape view chatbot height
    function handleResize() {
      if (window.innerHeight < 585) {
        // Set the div height to match the viewport height
        setIsLandscape(true);
      } else {
        setIsLandscape(false);
      }
    }

    // Add event listener on mount
    window.addEventListener("resize", handleResize);

    // Call handleResize immediately in case the initial viewport height is less than 520px
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
    //For landscape view chatbot height
  }, []);

  useEffect(() => {
    const JSONArray = JSON.stringify(liveChatsData);
    localStorage.setItem("userAgentChats", JSONArray);
    localStorage.setItem("step", step);
  }, [liveChatsData, step]);

  useEffect(() => {
    if (
      chatSessionHistory.length !== 0 &&
      !isLoading &&
      isStreamComplete &&
      currentAssistantChatMessage !== ""
    ) {
      setChatHistory(chatSessionHistory);
      setSessionChatHistory((pre) => [
        ...pre,
        {
          assistant: currentAssistantChatMessage,
          user_message: currentUserChatMessage,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionHistory, isLoading, isStreamComplete]);

  useEffect(() => {
    if (sessionChatHistory.length !== 0) {
      localStorage.setItem("chatMessage", JSON.stringify(sessionChatHistory));
    }
  }, [sessionChatHistory]);

  // For chat auto scroll ref
  const chatHistoryRef = useRef(null);

  const resetChatSession = () => {
    setChatValue("");
    setChatCreate("");
    setChatHistory([]);
    setCurrentAssistantChatMessage("");
    setCurrentUserChatMessage("");
    setIsTranscript(false);
    setAcceptTermsAndConditions(false);
    setIsMaximizeChatSize(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      //event.preventDefault();
      // Perform the action you want
      handleChatCreate();
    }
  };

  const handleTermsAndConditions = () => {
    setAcceptTermsAndConditions(true);
  };

  const handleEndChat = (value) => {
    if (step > 3 && value) {
      disconnectAgentChat(chatCreate).then(() => {
        setLiveChatsData([]);
        setCurrentAssistantChatMessage("");
        setCurrentUserChatMessage("");
        resetChatSession();
      });
    } else if (value) {
      clearChatLocalStorage();
      setLiveChatsData([]);
      setCurrentAssistantChatMessage("");
      setCurrentUserChatMessage("");
      resetChatSession();
    }
  };

  // Close chat when escape is pressed
  const handleEscapeKeyPress = (event) => {
    if (event.key === "Escape") {
      handleIsChatWindowOpen(false);
    }
  };

  // Function to trap focus within the chat window
  const trapFocus = (event) => {
    if (event.key === "Tab") {
      if (event.shiftKey) {
        /* shift + tab */ if (
          document.activeElement === firstFocusableRef.current
        ) {
          lastFocusableRef.current.focus();
          event.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableRef.current) {
          firstFocusableRef.current.focus();
          event.preventDefault();
        }
      }
    }
  };

  // For setting focus
  useEffect(() => {
    if (
      (isChatWindowOpen || !acceptTermsAndConditions) &&
      firstFocusableRef.current
    ) {
      firstFocusableRef.current.focus();
      window.addEventListener("keydown", handleEscapeKeyPress);
    }
    if (isChatWindowOpen) {
      window.addEventListener("keydown", trapFocus);
    }
    return () => {
      window.removeEventListener("keydown", handleEscapeKeyPress);
      window.removeEventListener("keydown", trapFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatWindowOpen, acceptTermsAndConditions]);

  // For going back to chat from transcript
  const handleBackToChat = () => {
    setIsTranscript(false);
    setHasChatEnded(false);
  };

  // For chat auto scroll
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [chatHistory, currentUserChatMessage, currentAssistantChatMessage]);

  const handleTranscriptShowHide = () => {
    setIsTranscript((prev) => !prev);
    setHasChatEnded(false);
  };

  //Prevent scroll on mobile devices when the chat window is open.
  useEffect(() => {
    if (isChatWindowOpen && window.innerWidth < 410) {
      document.body.style.overflow = isChatWindowOpen ? "hidden" : "";
    }

    // Cleanup function to reset the style when the component unmounts
    return () => {
      document.body.style.overflow = "";
    };
  }, [isChatWindowOpen]);

  const handleEnter = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleUserData();
    }
  };

  useEffect(() => {
    if (userAgentDetails.is_chat_complete === true) {
      setLiveChatsData((pre) => [
        ...pre,
        {
          message:
            "This chat was ended by the agent. Please click ❌ to provide your feedback and close the chat.",
          sender: "agent",
        },
      ]);
      setIsRequestTypeAgent("");
      setStep(1);
      localStorage.setItem("isCompletedChat", true);
    }
  }, [userAgentDetails]);

  const handleCloseChat = () => {
    handleIsChatWindowOpen(false);
    setIsTranscript(false);
    setHasChatEnded(false);
  };

  const handleCrossButton = () => {
    if (hasChatEnded) {
      handleEndChat(true);
      clearChatLocalStorage();
      handleCloseChat();
    } else {
      setHasChatEnded(true);
    }
  };

  useInactivityTracker(() => {
    handleCloseChat();
    handleEndChat(true);
    clearChatLocalStorage();
  });
  const isChatCompleted = localStorage.getItem("isCompletedChat");

  return (
    <>
      <div
        className={`curai-box-border curai-h-[65px] curai-w-full`}
        ref={firstFocusableRef}
        tabIndex={0}
        aria-label="Chat Widget Window"
        role="dialog"
      >
        <div
          className={`curai-box-border curai-text-white curai-rounded-t-lg curai-w-full curai-h-full curai-justify-between curai-flex curai-bg-primary`}
          style={{ backgroundColor: colorOne }}
          aria-label="Chat Widget Navigation Region"
          role="region"
        >
          <div
            className={`curai-box-border curai-truncate curai-max-w-[225px] ${
              isMaximizeChatSize
                ? "curai-flex curai-items-center "
                : "curai-flex curai-items-center curai-w-full"
            }`}
          >
            {isBrandLoading ? (
              <div
                className={`${
                  isMaximizeChatSize
                    ? "curai-flex curai-items-center"
                    : "curai-flex curai-items-center curai-w-full"
                }`}
              >
                <Skeleton
                  count={1}
                  height={33}
                  width={33}
                  className="!curai-rounded-full curai-ml-5"
                />
                <Skeleton
                  count={1}
                  height={20}
                  width={120}
                  className="curai-ml-5"
                />
              </div>
            ) : (
              <>
                {logoUrl ? (
                  <img
                    src={`${logoUrl}`}
                    alt=""
                    className="curai-h-[33px] curai-ml-5"
                    aria-hidden={true}
                  />
                ) : (
                  <div className="curai-relative curai-flex curai-items-center curai-justify-center curai-overflow-hidden curai-rounded-full dark:curai-bg-gray-600 curai-ml-5">
                    <img
                        src={`${process.env.REACT_APP_EMBED_URL}/assets/chat-icon-light.svg`}
                        alt=""
                        className="curai-w-10 curai-h-10"
                      />
                  </div>
                )}
              </>
            )}
            {title && (
              <p className="curai-ml-5 curai-text-l curai-mx-0 curai-mr-0 curai-max-w-[150px] curai-overflow-hidden curai-text-ellipsis">
                {title}
              </p>
            )}
          </div>
          <div className="curai-relative curai-flex curai-justify-end curai-mr-5 curai-gap-4 curai-items-center">
            {!branding?.ule?.is_tue &&
              branding?.global?.isEmailTranscriptVisible !== false && (
                <Email
                  handleTranscriptShowHide={handleTranscriptShowHide}
                  acceptTermsAndConditions={acceptTermsAndConditions}
                />
              )}
            <div className="curai-hidden md:curai-block">
              {isMaximizeChatSize ? (
                <Maximize2 setIsMaximizeChatSize={setIsMaximizeChatSize} />
              ) : (
                <Minimize2 setIsMaximizeChatSize={setIsMaximizeChatSize} />
              )}
            </div>

            <ChevronDown handleChatToggle={handleChatToggle} />
            <button
              onClick={handleCrossButton}
              title="Close"
              id="curatu-close-chat"
              className="hover:curai-ring-1 hover:curai-ring-white focus:curai-box-border curai-border-none curai-text-white curai-bg-transparent curai-p-0"
            >
              <Cancel />
            </button>
          </div>
        </div>
      </div>
      <div
        className={`curai-w-full curai-p-5 curai-rounded-b-lg curai-shadow-xl curai-box-border curai-bg-white curai-overflow-auto curai-flex curai-flex-col curai-h-full 
        `}
        aria-label="Chat Widget Main Region"
        role="region"
      >
        {hasChatEnded ? (
          userAgentDetails.agent_assigned ||
          userAgentDetails.is_chat_complete ||
          isChatCompleted ? (
            <EndChat
              onEndChat={handleEndChat}
              brandPrimaryColor={colorOne}
              ref={lastFocusableRef}
              apiKey={apiKey}
              chatId={chatCreate}
              orgId={orgId}
              isAgentAssigned={userAgentDetails.agent_assigned}
              handleCloseChat={handleCloseChat}
              isChatCompleted={isChatCompleted}
              handleBackToChat={handleBackToChat}
              userAgentDetails={userAgentDetails}
              isDisconnectChatLoading={isDisconnectChatLoading}
            />
          ) : (
            <ChatBotEndChat
              onEndChat={handleEndChat}
              handleCloseChat={handleCloseChat}
              brandPrimaryColor={colorOne}
              ref={lastFocusableRef}
              handleBackToChat={handleBackToChat}
              branding={branding}
            />
          )
        ) : (
          <>
            {!acceptTermsAndConditions ? (
              <TermsAndConditions
                onTermsAndConditionsChange={handleTermsAndConditions}
                termsAndConditionsCopy={tnc}
                brandPrimaryColor={colorOne}
                ref={lastFocusableRef}
                isBrandLoading={isBrandLoading}
                isMaximizeChatSize={isMaximizeChatSize}
                isLandscape={isLandscape}
              />
            ) : (
              <>
                {isTranscript || hasChatEnded ? (
                  <Transcript
                    chatId={chatCreate}
                    apiKey={apiKey}
                    isLandscape={isLandscape}
                    brandPrimaryColor={colorOne}
                    ref={lastFocusableRef}
                    onBackToChat={handleBackToChat}
                    isMaximizeChatSize={isMaximizeChatSize}
                    widgetKey={widgetKey}
                  />
                ) : (
                  <div className="curai-flex curai-flex-col curai-h-full curai-w-full">
                    <div
                      className={`curai-flex-grow curai-overflow-auto`}
                      tabIndex={0}
                      ref={chatHistoryRef}
                    >
                      <div className={` curai-text-left`}>
                        <div className="curai-flex">
                          <span aria-hidden={true}>
                            <PeopleIcon
                              className="curai-mr-1"
                              color={colorOne}
                            />
                          </span>
                          <div
                            className={`curai-w-fit curai-bg-[#f4f4f4] curai-mb-1 curai-text-base curai-text-black curai-leading-[22px] curai-break-words curai-p-1 curai-rounded-t-lg curai-rounded-br-lg 
                          `}
                          style={{ backgroundColor: colorTwo }}
                          >
                            {message ? message : "Hi there!"}
                          </div>
                        </div>
                        <div className="curai-text-xs curai-ml-8 curai-text-black">
                          Bot
                        </div>
                      </div>
                      {chatHistory.length > 0 &&
                        chatHistory?.map((item, index) => {
                          const { user_message, assistant } = item;
                          return (
                            user_message &&
                            assistant && (
                              <div key={`chat-${index}`}>
                                <>
                                  {" "}
                                  <UserMessage
                                    message={user_message}
                                    brandColor={colorOne}
                                    isMaximizeChatSize={isMaximizeChatSize}
                                  />
                                  <SystemMessage
                                    message={
                                      <Markdown
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={safeMarkdownPlugins}
                                        components={{ ...safeMarkdownComponents, a: LinkRenderer }}
                                      >
                                        {assistant
                                          .replace(/\|\s*\|/g, "|\n|")
                                          .replace(/^UU$/, "Thinking...")}
                                      </Markdown>
                                    }
                                    brandColor={colorTwo}
                                    currentMessage={false}
                                    brandColorPrimary={colorOne}
                                    isMaximizeChatSize={isMaximizeChatSize}
                                  />
                                </>
                              </div>
                            )
                          );
                        })}
                      <div ref={chatEndRef} />
                      {currentUserChatMessage && (
                        <>
                          <UserMessage
                            message={currentUserChatMessage}
                            brandColor={colorOne}
                            isMaximizeChatSize={isMaximizeChatSize}
                          />
                        </>
                      )}
                      {currentAssistantChatMessage && (
                        <>
                          <SystemMessage
                            loading={isLoading}
                            message={
                              <Markdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={safeMarkdownPlugins}
                                components={{ ...safeMarkdownComponents, a: LinkRenderer }}
                              >
                                {currentAssistantChatMessage
                                  .replace(/\|\|/g, "|\n|")
                                  .replace(/^UU$/, "Thinking...")}
                              </Markdown>
                            }
                            brandColor={colorTwo}
                            currentMessage={true}
                            brandColorPrimary={colorOne}
                            isMaximizeChatSize={isMaximizeChatSize}
                          />
                        </>
                      )}

                      {liveChatsData.map((val, index) =>
                        val.sender !== "agent" ? (
                          <Fragment key={index}>
                            <UserMessage
                              message={val.message}
                              brandColor={colorOne}
                              isMaximizeChatSize={isMaximizeChatSize}
                            />
                          </Fragment>
                        ) : (
                          <Fragment key={index}>
                            <SystemMessage
                              loading={isLoading}
                              message={
                                <Markdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={safeMarkdownPlugins}
                                  components={{ ...safeMarkdownComponents, a: LinkRenderer }}
                                >
                                  {val.message
                                    .replace(/\|\s*\|/g, "|\n|")
                                    .replace(/^UU$/, "Thinking...")}
                                </Markdown>
                              }
                              brandColor={colorTwo}
                              currentMessage={true}
                              brandColorPrimary={colorOne}
                              isMaximizeChatSize={isMaximizeChatSize}
                              agentName={val.agent_name}
                              index={index}
                            />
                          </Fragment>
                        ),
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="curai-relative curai-pt-6">
                      {userAgentDetails?.typingStatus?.agent && (
                        <p
                          className="curai-absolute curai-top-0 curai-text-sm curai-text-left curai-pl-[2px] curai-pb-1.5 !curai-text-headingGrey"
                          style={{ margin: "0px" }}
                        >
                          {agentFirstName} is typing...
                        </p>
                      )}
                      <InputTextArea
                        isMaximizeChatSize={isMaximizeChatSize}
                        sendMessageContainerRef={sendMessageContainerRef}
                        isRequestTypeAgent={isRequestTypeAgent}
                        chatValue={chatValue}
                        handleKeyPress={handleKeyPress}
                        isLoading={isLoading}
                        hoverNoneCss={hoverNoneCss}
                        lastFocusableRef={lastFocusableRef}
                        handleChatCreate={handleChatCreate}
                        colorOne={colorOne}
                        handleEnter={handleEnter}
                        step={step}
                        handleUserData={handleUserData}
                        setChatValue={setChatValue}
                        chatCreate={chatCreate}
                        getUserAgentDetails={getUserAgentDetails}
                        orgId={orgId}
                        setLiveChatsData={setLiveChatsData}
                        setTextareaHeight={setTextareaHeight}
                        textareaHeight={textareaHeight}
                        textareaMaximizeHeight={textareaMaximizeHeight}
                        setTextareaMaximizeHeight={setTextareaMaximizeHeight}
                        brandColor={colorTwo}
                        userAgentDetails={userAgentDetails}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
